import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Lock, LogOut, Plus, Pencil, Trash2, Save, X, Search,
  Wallet, ArrowUpRight, ArrowDownRight, TrendingUp, Printer, Users, Upload, Database, FileText,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENT_AY_DEFAULT = 'AY 2026-27';
const CATEGORIES = [
  'Salaries',
  'Small Businesses',
  'Tax Audits',
  'GST & Accounting',
  'PF-ESI-PT',
  'Others',
];
const TAB_GROUPS = [
  { key: 'yearly',  label: 'Yearly',            members: ['Salaries', 'Small Businesses', 'Tax Audits'] },
  { key: 'monthly', label: 'Monthly / Quarterly', members: ['GST & Accounting', 'PF-ESI-PT'] },
  { key: 'mix',     label: 'Mixed',             members: ['Others'] },
];
const DEFAULT_CATEGORY = 'Small Businesses';
// Yearly heads where every client is billed together → allow setting one billing date for the whole tab.
const BULK_BILLING_CATEGORIES = ['Salaries', 'Small Businesses', 'Tax Audits'];
const AGEING_TAB = 'Ageing Schedule';
const AGE_BUCKETS = [
  { key: 'b0_30',  label: '0–30 days',  min: 0,  max: 30,       tone: 'text-emerald-700', bar: 'bg-emerald-500' },
  { key: 'b31_60', label: '31–60 days', min: 31, max: 60,       tone: 'text-amber-700',   bar: 'bg-amber-500' },
  { key: 'b61_90', label: '61–90 days', min: 61, max: 90,       tone: 'text-orange-700',  bar: 'bg-orange-500' },
  { key: 'b90',    label: '90+ days',   min: 91, max: Infinity, tone: 'text-red-700',     bar: 'bg-red-500' },
];
const bucketFor = (days) => {
  if (days <= 30) return 'b0_30';
  if (days <= 60) return 'b31_60';
  if (days <= 90) return 'b61_90';
  return 'b90';
};
const TASK_STATUS_LABEL = { C: 'Completed', IP: 'In Progress', P: 'Pending' };
const TASK_STATUS_TONE = {
  C: 'bg-green-50 text-green-700 border-green-200',
  IP: 'bg-amber-50 text-amber-700 border-amber-200',
  P: 'bg-gray-100 text-gray-600 border-gray-200',
};
const PAYMENT_TONE = {
  Received: 'bg-green-100 text-green-800 border-green-200',
  Pending: 'bg-red-50 text-red-700 border-red-200',
};

const money = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

const ENTITY_OPTIONS = ['RST & Co', 'SCCS LLP', 'SCCL Mithun', 'SCCS Shilpa'];

// Categories that default to a specific billing entity
const DEFAULT_ENTITY_BY_CATEGORY = {
  'GST & Accounting': 'SCCS LLP',
  'PF-ESI-PT': 'SCCS LLP',
};
const defaultEntityFor = (category) => DEFAULT_ENTITY_BY_CATEGORY[category] || 'RST & Co';


const emptyEntry = (ay = CURRENT_AY_DEFAULT, category = DEFAULT_CATEGORY) => ({
  assessment_year: ay,
  category,
  client_name: '',
  work_alloted_to: '',
  task_status: 'C',
  billing_under: defaultEntityFor(category),
  fees: 0,
  payment_status: 'Pending',
  payment_date: '',
  billing_date: '',
  notes: '',
  sort_order: 100,
});

const formatError = (detail) => {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const toISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return '';
};
const fromISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
};

const CashflowPage = () => {
  const { user, ready, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ entities: [], categories: [], total_billed: 0, total_received: 0, total_pending: 0, count: 0 });
  const [years, setYears] = useState([CURRENT_AY_DEFAULT]);
  const [ay, setAy] = useState(CURRENT_AY_DEFAULT);
  const [tab, setTab] = useState(DEFAULT_CATEGORY);   // active category tab
  const [ageingHead, setAgeingHead] = useState('All'); // sub-page within Ageing Schedule
  const [bulkBillDate, setBulkBillDate] = useState(''); // ISO date for bulk billing-date apply
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [entityFilter, setEntityFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [q, setQ] = useState('');

  // Editor
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);
  const csvFileRef = useRef(null);

  // Import modal
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importAY, setImportAY] = useState(CURRENT_AY_DEFAULT);
  const [importDefaultCategory, setImportDefaultCategory] = useState(DEFAULT_CATEGORY);
  const [importBatches, setImportBatches] = useState([]);
  const [deletingBatch, setDeletingBatch] = useState(null);

  useEffect(() => {
    if (ready && !user) navigate('/admin/login?next=/admin/cashflow', { replace: true });
  }, [ready, user, navigate]);

  const load = async (year = ay) => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/cashflow`, { params: { assessment_year: year } });
      setItems(data.items || []);
      setSummary(data.summary || { entities: [], categories: [], total_billed: 0, total_received: 0, total_pending: 0, count: 0 });
      const merged = Array.from(new Set([CURRENT_AY_DEFAULT, ...(data.assessment_years || [])]));
      setYears(merged);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (ready && user) load(ay); /* eslint-disable-next-line */ }, [ready, user, ay]);

  // Sorting
  const [sortKey, setSortKey] = useState(null);   // 'client_name' | 'fees' | null
  const [sortDir, setSortDir] = useState('asc');  // 'asc' | 'desc'

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    // third click clears sort
    setSortKey(null); setSortDir('asc');
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const base = items.filter((it) => {
      const cat = it.category || DEFAULT_CATEGORY;
      if (cat !== tab) return false;
      if (entityFilter && it.billing_under !== entityFilter) return false;
      if (paymentFilter && it.payment_status !== paymentFilter) return false;
      if (needle) {
        const hay = `${it.client_name || ''} ${it.work_alloted_to || ''} ${it.billing_under || ''} ${it.notes || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    if (!sortKey) return base;
    const dir = sortDir === 'desc' ? -1 : 1;
    const val = (r) => {
      if (sortKey === 'fees') return Number(r.fees) || 0;
      return (r[sortKey] || '').toString().toLowerCase();
    };
    return [...base].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  }, [items, tab, entityFilter, paymentFilter, q, sortKey, sortDir]);

  const filteredTotals = useMemo(() => {
    const t = { billed: 0, received: 0, pending: 0 };
    filtered.forEach((r) => {
      const f = Number(r.fees) || 0;
      t.billed += f;
      if (r.payment_status === 'Received') t.received += f; else t.pending += f;
    });
    return t;
  }, [filtered]);

  // Redundant clients within the current head (category tab), for this AY.
  const dupClients = useMemo(() => {
    const norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const counts = new Map();
    items
      .filter((r) => (r.category || DEFAULT_CATEGORY) === tab)
      .forEach((r) => {
        const k = norm(r.client_name);
        if (!k) return;
        counts.set(k, (counts.get(k) || 0) + 1);
      });
    const out = [];
    items
      .filter((r) => (r.category || DEFAULT_CATEGORY) === tab)
      .forEach((r) => {
        const k = norm(r.client_name);
        if (counts.get(k) > 1 && !out.find((o) => o.key === k)) {
          out.push({ key: k, name: r.client_name, count: counts.get(k) });
        }
      });
    return out;
  }, [items, tab]);

  // Ageing of all outstanding (Pending) fees across every category, aged from
  // the entry's created date. Buckets: 0–30 / 31–60 / 61–90 / 90+ days.
  const ageing = useMemo(() => {
    const now = Date.now();
    const blank = () => ({ b0_30: 0, b31_60: 0, b61_90: 0, b90: 0, total: 0, count: 0 });
    const totals = blank();
    const byCategory = {};
    const rows = [];
    items
      .filter((r) => r.payment_status === 'Pending' && (Number(r.fees) || 0) > 0)
      .forEach((r) => {
        let created;
        const bd = r.billing_date && String(r.billing_date).match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (bd) created = new Date(Number(bd[3]), Number(bd[2]) - 1, Number(bd[1])).getTime();
        else created = r.created_at ? new Date(r.created_at).getTime() : now;
        const ageDays = Math.max(0, Math.floor((now - created) / 86400000));
        const b = bucketFor(ageDays);
        const fee = Number(r.fees) || 0;
        const cat = r.category || DEFAULT_CATEGORY;
        if (!byCategory[cat]) byCategory[cat] = blank();
        [totals, byCategory[cat]].forEach((acc) => {
          acc[b] += fee; acc.total += fee; acc.count += 1;
        });
        rows.push({ ...r, ageDays, bucket: b, fee });
      });
    rows.sort((a, b) => b.ageDays - a.ageDays);
    const catList = CATEGORIES
      .filter((c) => byCategory[c])
      .map((c) => ({ category: c, ...byCategory[c] }));
    return { totals, rows, catList };
  }, [items]);


  const openNew = () => { setEditing(emptyEntry(ay, tab)); setError(''); };
  const openEdit = (row) => { setEditing({ ...row }); setError(''); };
  const closeEdit = () => { setEditing(null); setError(''); };

  const save = async () => {
    if (!editing.client_name || !editing.billing_under) {
      setError('Client Name and Billing Under are required'); return;
    }
    // Block duplicate client within the same head (category + AY).
    const norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const cat = editing.category || DEFAULT_CATEGORY;
    const yr = editing.assessment_year || CURRENT_AY_DEFAULT;
    const clash = items.find(
      (r) => r.id !== editing.id
        && (r.category || DEFAULT_CATEGORY) === cat
        && (r.assessment_year || CURRENT_AY_DEFAULT) === yr
        && norm(r.client_name) === norm(editing.client_name)
    );
    if (clash) {
      setError(`"${editing.client_name}" already exists in the ${cat} tab. Duplicate clients aren't allowed in the same head.`);
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        assessment_year: editing.assessment_year || CURRENT_AY_DEFAULT,
        category: editing.category || DEFAULT_CATEGORY,
        client_name: editing.client_name,
        work_alloted_to: editing.work_alloted_to || null,
        task_status: editing.task_status || 'C',
        billing_under: editing.billing_under,
        fees: Number(editing.fees) || 0,
        payment_status: editing.payment_status || 'Pending',
        payment_date: editing.payment_date || null,
        billing_date: editing.billing_date || null,
        notes: editing.notes || null,
        sort_order: Number(editing.sort_order) || 100,
      };
      if (editing.id) await axios.put(`${API}/admin/cashflow/${editing.id}`, payload);
      else await axios.post(`${API}/admin/cashflow`, payload);
      setEditing(null);
      await load(payload.assessment_year);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete entry for "${row.client_name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/cashflow/${row.id}`);
      await load(ay);
    } catch (err) { setError(formatError(err?.response?.data?.detail)); }
  };

  const applyBulkBillingDate = async () => {
    if (!bulkBillDate) { setError('Pick a billing date first.'); return; }
    const display = fromISODate(bulkBillDate); // DD-MM-YYYY
    if (!window.confirm(`Set billing date ${display} for ALL "${tab}" entries in ${ay}?`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/cashflow/set-billing-date`, {
        assessment_year: ay, category: tab, billing_date: display,
      });
      await load(ay);
      setBulkBillDate('');
      alert(`Billing date ${display} applied to ${data.updated} "${tab}" entries.`);
    } catch (err) { setError(formatError(err?.response?.data?.detail)); }
  };

  const setBillingUnder = async (row, next) => {
    if (next === row.billing_under) return;
    const payload = {
      assessment_year: row.assessment_year || CURRENT_AY_DEFAULT,
      category: row.category || DEFAULT_CATEGORY,
      client_name: row.client_name,
      work_alloted_to: row.work_alloted_to || null,
      task_status: row.task_status || 'C',
      billing_under: next,
      fees: Number(row.fees) || 0,
      payment_status: row.payment_status || 'Pending',
      payment_date: row.payment_date || null,
      billing_date: row.billing_date || null,
      notes: row.notes || null,
      sort_order: row.sort_order || 100,
    };
    setItems((prev) => prev.map((r) => (r.id === row.id ? { ...r, billing_under: next } : r)));
    try {
      await axios.put(`${API}/admin/cashflow/${row.id}`, payload);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
      await load(ay); // resync on failure
    }
  };

  const setPaymentStatus = async (row, next) => {
    if (next === row.payment_status) return;
    const nextDate = next === 'Received' ? (row.payment_date || todayDisplay()) : (row.payment_date || null);
    const payload = {
      assessment_year: row.assessment_year || CURRENT_AY_DEFAULT,
      category: row.category || DEFAULT_CATEGORY,
      client_name: row.client_name,
      work_alloted_to: row.work_alloted_to || null,
      task_status: row.task_status || 'C',
      billing_under: row.billing_under,
      fees: Number(row.fees) || 0,
      payment_status: next,
      payment_date: nextDate,
      billing_date: row.billing_date || null,
      notes: row.notes || null,
      sort_order: row.sort_order || 100,
    };
    // Optimistic update — keeps the UI snappy and blink-free
    setItems((prev) => prev.map((r) => (r.id === row.id ? { ...r, payment_status: next, payment_date: nextDate } : r)));
    // Recompute the header/entity summary locally so KPI cards stay in sync without a refetch
    setSummary((prev) => {
      if (!prev) return prev;
      const fees = Number(row.fees) || 0;
      const wasReceived = row.payment_status === 'Received';
      const isReceived = next === 'Received';
      if (wasReceived === isReceived) return prev;
      const delta = isReceived ? fees : -fees;
      const bump = (bucket) => ({
        ...bucket,
        received: (Number(bucket.received) || 0) + delta,
        pending: (Number(bucket.pending) || 0) - delta,
      });
      return {
        ...prev,
        total_received: (Number(prev.total_received) || 0) + delta,
        total_pending: (Number(prev.total_pending) || 0) - delta,
        entities: (prev.entities || []).map((e) => (e.billing_under === row.billing_under ? bump(e) : e)),
        categories: (prev.categories || []).map((c) => (c.category === (row.category || DEFAULT_CATEGORY) ? bump(c) : c)),
      };
    });
    try {
      await axios.put(`${API}/admin/cashflow/${row.id}`, payload);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
      await load(ay); // resync on failure only
    }
  };

  const uniqueEntities = useMemo(() => {
    const s = new Set(items.map((i) => i.billing_under).filter(Boolean));
    return Array.from(s).sort();
  }, [items]);

  // ---- Bulk import handlers ----
  const loadImportBatches = async (yr) => {
    try {
      const { data } = await axios.get(`${API}/admin/cashflow/import-batches`, {
        params: { assessment_year: yr || importAY },
      });
      setImportBatches(data);
    } catch {
      // Non-fatal: the upload-history list is supplementary.
    }
  };

  const deleteImportBatch = async (batch) => {
    if (!window.confirm(`Remove upload "${batch.filename}" and delete the ${batch.live_rows} row(s) it added? This cannot be undone.`)) return;
    setDeletingBatch(batch.id);
    try {
      await axios.delete(`${API}/admin/cashflow/import-batches/${batch.id}`);
      await loadImportBatches(batch.assessment_year);
      await load(batch.assessment_year);
    } catch (err) {
      setImportResult({ ok: false, error: formatError(err?.response?.data?.detail) || 'Delete failed' });
    } finally { setDeletingBatch(null); }
  };

  const seedRstAY2026 = async () => {
    setImporting(true); setImportResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/cashflow/seed-rst-ay2026-27`);
      setImportResult({ ok: true, ...data });
      await load('AY 2026-27');
      setAy('AY 2026-27');
    } catch (err) {
      setImportResult({ ok: false, error: formatError(err?.response?.data?.detail) || 'Seed failed' });
    } finally { setImporting(false); }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    if (!importAY) { setImportResult({ ok: false, error: 'Please choose an Assessment Year first' }); return; }
    setImporting(true); setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('assessment_year', importAY);
      form.append('default_category', importDefaultCategory || DEFAULT_CATEGORY);
      const { data } = await axios.post(`${API}/admin/cashflow/import-file`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult({ ok: true, ...data });
      await load(importAY);
      setAy(importAY);
      await loadImportBatches(importAY);
    } catch (err) {
      setImportResult({ ok: false, error: formatError(err?.response?.data?.detail) || 'Import failed' });
    } finally {
      setImporting(false);
      if (csvFileRef.current) csvFileRef.current.value = '';
    }
  };

  const printPage = () => {
    if (!printRef.current) { window.print(); return; }
    window.print();
  };

  if (!ready || !user) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-4">
        <div className="text-center">
          <Lock className="text-brand-yellow mx-auto mb-4" size={32} />
          <p className="text-white/70 text-sm">Verifying access…</p>
        </div>
      </div>
    );
  }

  const realizationPct = summary.total_billed > 0
    ? Math.round((summary.total_received / summary.total_billed) * 100) : 0;

  return (
    <div className="min-h-screen bg-white" data-testid="cashflow-page">
      {/* Admin control bar */}
      <div className="bg-[#0a0f1e] border-b border-white/10 text-white sticky top-0 z-30 print:hidden">
        <div className="max-w-[1720px] mx-auto px-[5%] py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
              <Wallet size={14} className="text-brand-yellow" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-brand-yellow uppercase">Admin Dashboard</div>
              <div className="text-sm font-medium">Internal Cashflow · {tab} · {(summary.categories||[]).find(c=>c.category===tab)?.count || 0} records</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/team')}
              className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              data-testid="nav-team-btn"
            >
              <Users size={13} /> Team Directory
            </button>
            <button
              onClick={() => navigate('/admin/vehicles')}
              className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Office Vehicle Diary"
              data-testid="nav-vehicles-btn"
            >
              <span aria-hidden>🚗</span> Vehicles
            </button>
            <button
              onClick={() => navigate('/admin/payroll')}
              className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Payroll register"
              data-testid="nav-payroll-btn"
            >
              <span className="font-mono tracking-widest">₹</span> Payroll
            </button>
            <button
              onClick={() => navigate('/admin/insurances')}
              className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Insurance register"
              data-testid="nav-insurances-btn"
            >
              <span aria-hidden>🛡</span> Insurances
            </button>
            <button
              onClick={() => navigate('/admin/policies')}
              className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Policies & SOP"
              data-testid="nav-policies-btn"
            >
              <span aria-hidden>📘</span> Policies
            </button>
            <Button
              onClick={openNew}
              className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 rounded-none h-9 px-4 text-sm font-semibold"
              data-testid="cashflow-add-btn"
            >
              <Plus size={16} className="mr-1.5" /> Add Entry
            </Button>
            <button
              onClick={async () => {
                if (!window.confirm('Reclassify all Pending rows that have a date or note as Received? This targets rows where the source Status column had a comment other than "Pending".')) return;
                try {
                  const { data } = await axios.post(`${API}/admin/cashflow/reclassify-received`, null, { params: { assessment_year: ay } });
                  alert(`Reclassified ${data.reclassified} row(s) to Received.`);
                  await load(ay);
                } catch (err) { setError(formatError(err?.response?.data?.detail)); }
              }}
              className="inline-flex items-center gap-1.5 text-white/85 hover:text-brand-yellow text-xs border border-white/25 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Any Pending row with a date or note becomes Received"
              data-testid="cashflow-reclassify-btn"
            >
              ✓ Reclassify Received
            </button>
            <button
              onClick={() => { setImportOpen(true); setImportResult(null); setImportAY(ay || CURRENT_AY_DEFAULT); setImportDefaultCategory(tab || DEFAULT_CATEGORY); loadImportBatches(ay || CURRENT_AY_DEFAULT); }}
              className="inline-flex items-center gap-1.5 text-white/85 hover:text-brand-yellow text-xs border border-white/25 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Bulk import from CSV or one-click seed"
              data-testid="cashflow-import-btn"
            >
              <Upload size={13} /> Bulk Import
            </button>
            <button
              onClick={printPage}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"
              title="Print / Export PDF"
              data-testid="cashflow-print-btn"
            >
              <Printer size={13} /> Print
            </button>
            {isAdmin && (
              <button onClick={() => navigate('/admin/firm-documents')} className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors" title="Confidential Firm Documents" data-testid="nav-firm-docs-btn"><span aria-hidden>📁</span> Firm Docs</button>
            )}
            {isAdmin && (
              <button onClick={() => navigate('/admin/access')} className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors" title="User & Access Control" data-testid="nav-access-btn"><span aria-hidden>🔐</span> Access</button>
            )}
            <span className="hidden md:block text-xs text-white/50 border-l border-white/10 pl-3">{user.email}</span>
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              className="inline-flex items-center gap-1.5 bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 text-xs font-semibold transition-colors px-3 py-2"
              data-testid="cashflow-logout-btn"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative bg-[#0a0f1e] pt-14 pb-16 overflow-hidden print:hidden">
        <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[500px] bg-brand-yellow/15 blur-[160px] rounded-full pointer-events-none" />
        <div className="relative max-w-[1720px] mx-auto px-[5%]">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-4">
                <Wallet size={14} className="text-brand-yellow" />
                <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
                  Internal · R S T &amp; Co.
                </span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white leading-[1.02] tracking-tight">
                Internal Cashflow
              </h1>
              <p className="text-sm md:text-base text-white/60 mt-3 max-w-2xl">
                Client-fee billing &amp; collection register. Track what&rsquo;s billed, what&rsquo;s realised, and what&rsquo;s outstanding — grouped by entity.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-mono tracking-widest text-white/50 uppercase">AY</label>
              <select
                value={ay}
                onChange={(e) => setAy(e.target.value)}
                className="bg-white/5 border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-brand-yellow"
                data-testid="cashflow-ay-select"
              >
                {years.map((y) => <option key={y} value={y} className="text-gray-900">{y}</option>)}
              </select>
            </div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            <KPI label="Total Billed" value={`₹ ${money(summary.total_billed)}`} icon={<TrendingUp size={14} />} tone="yellow" />
            <KPI label="Realised" value={`₹ ${money(summary.total_received)}`} sub={`${realizationPct}% realization`} icon={<ArrowUpRight size={14} />} tone="green" />
            <KPI label="Outstanding" value={`₹ ${money(summary.total_pending)}`} icon={<ArrowDownRight size={14} />} tone="red" />
            <KPI label="Clients Billed" value={String(summary.count)} icon={<Users size={14} />} tone="mono" />
          </div>
        </div>
      </section>

      {/* Entity summary strip */}
      {summary.entities.length > 0 && (
        <section className="bg-gray-50 border-b border-gray-200 py-6 print:hidden">
          <div className="max-w-[1720px] mx-auto px-[5%]">
            <div className="font-mono text-[10px] tracking-[0.25em] text-gray-500 uppercase mb-3">
              Billing under · Entity-wise summary
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {summary.entities.map((e) => (
                <button
                  key={e.billing_under}
                  onClick={() => setEntityFilter(entityFilter === e.billing_under ? '' : e.billing_under)}
                  className={`text-left bg-white border transition-all p-4 ${
                    entityFilter === e.billing_under ? 'border-brand-blue ring-2 ring-brand-blue/20' : 'border-gray-200 hover:border-gray-400'
                  }`}
                  data-testid={`entity-chip-${e.billing_under}`}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <div className="font-heading font-bold text-gray-900 text-sm">{e.billing_under}</div>
                    <div className="font-mono text-[10px] text-gray-400">{e.count} rec</div>
                  </div>
                  <div className="font-heading text-2xl font-black text-gray-900 tabular-nums">₹ {money(e.billed)}</div>
                  <div className="flex items-center justify-between mt-2 text-[11px]">
                    <span className="text-green-700">✓ ₹{money(e.received)}</span>
                    <span className="text-red-600">◔ ₹{money(e.pending)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Category tabs — grouped by cadence */}
      <section className="bg-white border-b border-gray-200 sticky top-[60px] z-20 print:hidden">
        <div className="max-w-[1720px] mx-auto px-[5%] py-2 flex items-stretch gap-3 overflow-x-auto">
          {TAB_GROUPS.map((grp, gIdx) => (
            <React.Fragment key={grp.key}>
              {gIdx > 0 && (
                <div className="self-stretch w-px bg-gray-200 mx-1" aria-hidden />
              )}
              <div className="flex flex-col min-w-0">
                <div className={`font-mono text-[9px] tracking-[0.25em] uppercase mb-1 pl-1 ${
                  grp.key === 'yearly' ? 'text-brand-blue' :
                  grp.key === 'monthly' ? 'text-emerald-700' :
                  'text-gray-500'
                }`}>
                  {grp.label}
                </div>
                <div className="flex items-center gap-1.5">
                  {grp.members.map((cat) => {
                    const catSum = (summary.categories || []).find((c) => c.category === cat);
                    const count = catSum ? catSum.count : 0;
                    const billed = catSum ? catSum.billed : 0;
                    const active = tab === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => setTab(cat)}
                        className={`whitespace-nowrap inline-flex items-baseline gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                          active
                            ? 'border-brand-blue text-gray-900 bg-brand-blue/[0.04]'
                            : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
                        }`}
                        data-testid={`cashflow-tab-${cat}`}
                      >
                        <span>{cat}</span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${
                          active ? 'bg-brand-yellow/20 border-amber-300 text-amber-800' : 'bg-gray-100 border-gray-200 text-gray-500'
                        }`}>
                          {count}
                        </span>
                        {billed > 0 && (
                          <span className="hidden md:inline text-[10px] font-mono text-gray-400 tabular-nums">₹{money(billed)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </React.Fragment>
          ))}
          <div className="self-stretch w-px bg-gray-200 mx-1" aria-hidden />
          <div className="flex flex-col min-w-0">
            <div className="font-mono text-[9px] tracking-[0.25em] uppercase mb-1 pl-1 text-red-600">Receivables</div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setTab(AGEING_TAB)}
                className={`whitespace-nowrap inline-flex items-baseline gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                  tab === AGEING_TAB
                    ? 'border-red-500 text-gray-900 bg-red-500/[0.05]'
                    : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
                }`}
                data-testid="cashflow-tab-ageing"
              >
                <span>{AGEING_TAB}</span>
                <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${
                  tab === AGEING_TAB ? 'bg-red-100 border-red-300 text-red-800' : 'bg-gray-100 border-gray-200 text-gray-500'
                }`}>
                  {ageing.totals.count}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      {tab !== AGEING_TAB && (
      <section className="bg-white border-b border-gray-200 py-4 sticky top-[126px] z-20 print:hidden">
        <div className="max-w-[1720px] mx-auto px-[5%] flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search client, work-allotted, notes…"
              className="w-full border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-gray-900"
              data-testid="cashflow-search"
            />
          </div>
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 text-sm bg-white"
            data-testid="cashflow-entity-filter"
          >
            <option value="">All entities</option>
            {uniqueEntities.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 text-sm bg-white"
            data-testid="cashflow-payment-filter"
          >
            <option value="">All statuses</option>
            <option value="Received">Received</option>
            <option value="Pending">Pending</option>
          </select>
          {(entityFilter || paymentFilter || q) && (
            <button
              onClick={() => { setEntityFilter(''); setPaymentFilter(''); setQ(''); }}
              className="text-xs text-gray-600 hover:text-red-600 underline"
              data-testid="cashflow-clear-filters"
            >
              Clear filters
            </button>
          )}
          <div className="ml-auto font-mono text-[11px] text-gray-500">
            Showing <b className="text-gray-900">{filtered.length}</b> of {items.length} · Billed
            <b className="text-gray-900"> ₹ {money(filteredTotals.billed)}</b>
          </div>
        </div>
        {BULK_BILLING_CATEGORIES.includes(tab) && (
          <div className="max-w-[1720px] mx-auto px-[5%] mt-2 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2" data-testid="bulk-billing-bar">
            <span className="font-mono text-[10px] tracking-widest uppercase text-brand-blue">Set billing date for all “{tab}”</span>
            <input
              type="date"
              value={bulkBillDate}
              onChange={(e) => setBulkBillDate(e.target.value)}
              className="border border-gray-300 px-3 py-1.5 text-sm bg-white"
              data-testid="bulk-billing-date-input"
            />
            <button
              onClick={applyBulkBillingDate}
              className="inline-flex items-center gap-1.5 bg-brand-blue hover:bg-brand-blue-hover text-white text-xs font-semibold px-3 py-2 transition-colors"
              data-testid="bulk-billing-apply-btn"
            >
              Apply to all {tab} entries
            </button>
            <span className="text-[11px] text-gray-400">These heads bill every client together — one date sets them all.</span>
          </div>
        )}
      </section>
      )}

      {/* Register table */}
      {tab !== AGEING_TAB && (
      <section className="py-8 print:pt-0" ref={printRef}>
        <div className="max-w-[1720px] mx-auto px-[5%]">
          <div className="hidden print:block mb-4">
            <h2 className="text-xl font-bold">R S T &amp; Co. — IT Filing / Cashflow Register — {ay}</h2>
          </div>

          {loading && (
            <div className="py-16 flex justify-center"><Loader2 size={26} className="animate-spin text-gray-400" /></div>
          )}
          {error && !editing && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-4 py-3 mb-4">{error}</div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-16 border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm">
              No entries {items.length ? 'match current filters' : `for ${ay}`}. Click <b>Add Entry</b> to start.
            </div>
          )}

          {!loading && filtered.length > 0 && (() => {
            // Split into two columns so twice as many rows fit on screen
            const mid = Math.ceil(filtered.length / 2);
            const columns = [filtered.slice(0, mid), filtered.slice(mid)];
            return (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6">
                {columns.map((rows, colIdx) => (
                  rows.length === 0 ? null : (
                  <div key={colIdx} className="border border-gray-200">
                    <table className="w-full text-sm table-fixed">
                      <thead className="bg-gray-900 text-white text-[11px] font-mono tracking-widest uppercase sticky top-[196px] z-30 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.4)]">
                        <tr>
                          <th className="text-left px-3 py-3 w-12 whitespace-nowrap">Sl</th>
                          <th
                            className="text-left px-3 py-3 whitespace-nowrap cursor-pointer select-none hover:bg-white/10"
                            onClick={() => toggleSort('client_name')}
                            data-testid="sort-client-name"
                            title="Sort by client name"
                          >
                            <span className="inline-flex items-baseline gap-1.5">
                              Client Name
                              <SortArrow active={sortKey === 'client_name'} dir={sortDir} />
                            </span>
                          </th>
                          <th className="text-left px-2 py-3 w-32 whitespace-nowrap">Under</th>
                          <th
                            className="text-right px-2 py-3 w-24 whitespace-nowrap cursor-pointer select-none hover:bg-white/10"
                            onClick={() => toggleSort('fees')}
                            data-testid="sort-fees"
                            title="Sort by fee amount"
                          >
                            <span className="inline-flex items-baseline gap-1.5 justify-end w-full">
                              Fees (₹)
                              <SortArrow active={sortKey === 'fees'} dir={sortDir} />
                            </span>
                          </th>
                          <th className="text-center px-2 py-3 w-14 whitespace-nowrap">Status</th>
                          <th className="text-left px-2 py-3 w-28 whitespace-nowrap">Date</th>
                          <th className="text-right px-2 py-3 w-16 print:hidden whitespace-nowrap">Act.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rows.map((r, i) => {
                          const slNo = colIdx === 0 ? i + 1 : mid + i + 1;
                          return (
                            <tr key={r.id} className="hover:bg-gray-50 group text-[13px]" data-testid={`cashflow-row-${r.id}`}>
                              <td className="px-3 py-0.5 text-gray-500 font-mono text-[12px] tabular-nums">{slNo}</td>
                              <td className="px-3 py-0.5 font-medium text-gray-900 truncate" title={r.client_name}>{r.client_name}</td>
                              <td className="px-3 py-0.5">
                                <select
                                  value={r.billing_under}
                                  onChange={(e) => setBillingUnder(r, e.target.value)}
                                  className="text-[12px] w-full bg-transparent border border-transparent hover:border-gray-300 focus:border-brand-blue focus:outline-none px-1 py-0 cursor-pointer text-gray-800"
                                  title="Change billing entity"
                                  data-testid={`billing-under-${r.id}`}
                                >
                                  {Array.from(new Set([...ENTITY_OPTIONS, r.billing_under].filter(Boolean))).map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-0.5 text-right tabular-nums font-medium">{money(r.fees)}</td>
                              <td className="px-2 py-0.5 text-center">
                                <select
                                  value={r.payment_status}
                                  onChange={(e) => setPaymentStatus(r, e.target.value)}
                                  className={`text-[11px] font-mono font-bold tracking-wider uppercase px-1 py-0 border cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-blue/30 w-9 text-center ${PAYMENT_TONE[r.payment_status] || PAYMENT_TONE.Pending}`}
                                  title={`${r.payment_status} — click to change`}
                                  data-testid={`payment-status-${r.id}`}
                                >
                                  <option value="Pending">P</option>
                                  <option value="Received">R</option>
                                </select>
                              </td>
                              <td className="px-3 py-0.5 text-gray-600 tabular-nums text-[12px]">{r.payment_date || '—'}</td>
                              <td className="px-3 py-0.5 print:hidden">
                                <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => openEdit(r)} className="p-0.5 border border-gray-300 text-gray-600 hover:border-brand-blue hover:text-brand-blue" data-testid={`edit-${r.id}`}>
                                    <Pencil size={10} />
                                  </button>
                                  <button onClick={() => remove(r)} className="p-0.5 border border-gray-300 text-gray-600 hover:border-red-500 hover:text-red-600" data-testid={`delete-${r.id}`}>
                                    <Trash2 size={10} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )
                ))}
                {/* Combined totals footer spans full width */}
                <div className="xl:col-span-2 border border-gray-900 bg-gray-50 px-3 py-2 flex flex-wrap items-center justify-between gap-3 text-xs">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-gray-600">
                    {tab} · Total <b className="font-heading text-gray-900 text-sm ml-2">{filtered.length}</b> rows
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-green-700">Received <b className="tabular-nums">₹{money(filteredTotals.received)}</b></span>
                    <span className="text-red-600">Pending <b className="tabular-nums">₹{money(filteredTotals.pending)}</b></span>
                    <span className="text-gray-900 font-heading text-sm">Billed <b className="tabular-nums">₹{money(filteredTotals.billed)}</b></span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Redundant-client notification for the current head */}
          {!loading && dupClients.length > 0 && (
            <div className="mt-4 border border-amber-300 bg-amber-50 px-4 py-3" data-testid="dup-clients-notice">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-amber-600">⚠</span>
                <span className="font-mono text-[11px] tracking-widest uppercase text-amber-800 font-bold">
                  Duplicate clients in {tab} · {dupClients.length}
                </span>
              </div>
              <p className="text-xs text-amber-700 mb-2">
                These client names appear more than once in this head. Remove the redundant rows to keep the register clean.
              </p>
              <div className="flex flex-wrap gap-2">
                {dupClients.map((d) => (
                  <span key={d.key} className="inline-flex items-center gap-1.5 text-xs bg-white border border-amber-300 px-2 py-1"
                    data-testid={`dup-client-${d.key}`}>
                    <b className="text-gray-900">{d.name}</b>
                    <span className="text-amber-700 font-mono">×{d.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Print-friendly billing-under summary */}
          {!loading && summary.entities.length > 0 && (
            <div className="mt-8 hidden print:block">
              <h3 className="font-bold mb-2">Billing Under — Summary</h3>
              <table className="w-full text-sm border border-gray-800">
                <thead><tr className="bg-gray-100">
                  <th className="text-left px-2 py-1 border border-gray-300">Billing Under</th>
                  <th className="text-right px-2 py-1 border border-gray-300">Amount (₹)</th>
                </tr></thead>
                <tbody>
                  {summary.entities.map((e) => (
                    <tr key={e.billing_under}>
                      <td className="px-2 py-1 border border-gray-300">{e.billing_under}</td>
                      <td className="px-2 py-1 border border-gray-300 text-right">{money(e.billed)}</td>
                    </tr>
                  ))}
                  <tr className="font-bold bg-gray-100">
                    <td className="px-2 py-1 border border-gray-300">Total</td>
                    <td className="px-2 py-1 border border-gray-300 text-right">{money(summary.total_billed)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Ageing Schedule */}
      {tab === AGEING_TAB && (
        <section className="py-8">
          <div className="max-w-[1720px] mx-auto px-[5%]">
            <div className="mb-6">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-red-600 mb-1">Receivables</div>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-gray-900 tracking-tight">Ageing of Outstandings</h2>
              <p className="text-sm text-gray-500 mt-1">
                All <b>Pending</b> fees for {ay}, aged by the number of days since the entry was recorded.
              </p>
            </div>

            {ageing.totals.count === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-300 bg-gray-50 text-gray-500 text-sm" data-testid="ageing-empty">
                🎉 No outstanding receivables for {ay}. Everything is collected.
              </div>
            ) : (() => {
              const heads = ageing.catList.map((c) => c.category);
              const blank = { b0_30: 0, b31_60: 0, b61_90: 0, b90: 0, total: 0, count: 0 };
              const headTotals = ageingHead === 'All'
                ? ageing.totals
                : (ageing.catList.find((c) => c.category === ageingHead) || blank);
              const headRows = ageingHead === 'All'
                ? ageing.rows
                : ageing.rows.filter((r) => (r.category || DEFAULT_CATEGORY) === ageingHead);
              return (
              <>
                {/* Head-of-income sub-pages */}
                <div className="flex items-center gap-1.5 overflow-x-auto border-b border-gray-200 mb-6" data-testid="ageing-head-tabs">
                  {['All', ...heads].map((h) => {
                    const active = ageingHead === h;
                    const amt = h === 'All' ? ageing.totals.total : (ageing.catList.find((c) => c.category === h)?.total || 0);
                    return (
                      <button
                        key={h}
                        onClick={() => setAgeingHead(h)}
                        className={`whitespace-nowrap inline-flex items-baseline gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                          active ? 'border-red-500 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
                        }`}
                        data-testid={`ageing-head-${h}`}
                      >
                        <span>{h === 'All' ? 'All Heads' : h}</span>
                        <span className={`text-[10px] font-mono tabular-nums ${active ? 'text-red-600' : 'text-gray-400'}`}>₹{money(amt)}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="font-heading text-lg font-bold text-gray-900 mb-3">
                  {ageingHead === 'All' ? 'All Heads of Income' : ageingHead}
                  <span className="ml-2 text-sm font-mono font-normal text-gray-400">· {headRows.length} outstanding · ₹{money(headTotals.total)}</span>
                </div>

                {/* Bucket summary cards (scoped to selected head) */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 mb-6">
                  {AGE_BUCKETS.map((b) => {
                    const amt = headTotals[b.key] || 0;
                    const pct = headTotals.total > 0 ? Math.round((amt / headTotals.total) * 100) : 0;
                    return (
                      <div key={b.key} className="bg-white p-4" data-testid={`ageing-bucket-${b.key}`}>
                        <div className={`font-mono text-[10px] tracking-widest uppercase mb-1 ${b.tone}`}>{b.label}</div>
                        <div className="font-heading text-xl md:text-2xl font-black text-gray-900 tabular-nums">₹ {money(amt)}</div>
                        <div className="mt-2 h-1.5 bg-gray-100 overflow-hidden">
                          <div className={`h-full ${b.bar}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-[10px] text-gray-400 mt-1 font-mono">{pct}% of outstanding</div>
                      </div>
                    );
                  })}
                </div>

                {/* By-category matrix — only on the All view */}
                {ageingHead === 'All' && (
                <div className="border border-gray-200 mb-8 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-900 text-white text-[11px] font-mono tracking-widest uppercase">
                      <tr>
                        <th className="text-left px-3 py-1.5">Head / Category</th>
                        {AGE_BUCKETS.map((b) => <th key={b.key} className="text-right px-3 py-1.5 whitespace-nowrap">{b.label}</th>)}
                        <th className="text-right px-3 py-1.5">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ageing.catList.map((c) => (
                        <tr key={c.category} className="hover:bg-gray-50 cursor-pointer" onClick={() => setAgeingHead(c.category)} data-testid={`ageing-cat-${c.category}`}>
                          <td className="px-3 py-1 font-medium text-gray-900">{c.category}</td>
                          {AGE_BUCKETS.map((b) => (
                            <td key={b.key} className="px-3 py-1 text-right tabular-nums text-gray-700">{c[b.key] ? `₹${money(c[b.key])}` : '—'}</td>
                          ))}
                          <td className="px-3 py-1 text-right tabular-nums font-semibold text-gray-900">₹{money(c.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t-2 border-gray-900 font-bold">
                        <td className="px-3 py-1.5 font-mono text-[11px] tracking-widest uppercase text-gray-700">Total</td>
                        {AGE_BUCKETS.map((b) => (
                          <td key={b.key} className="px-3 py-1.5 text-right tabular-nums text-gray-900">₹{money(ageing.totals[b.key])}</td>
                        ))}
                        <td className="px-3 py-1.5 text-right tabular-nums text-red-700">₹{money(ageing.totals.total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                )}

                {/* Detailed outstanding list (scoped to selected head) */}
                <div className="font-mono text-[10px] tracking-widest uppercase text-gray-500 mb-2">
                  Outstanding items · {headRows.length} · oldest first
                </div>
                <div className="border border-gray-200 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-gray-700 text-[11px] font-mono tracking-widest uppercase">
                      <tr>
                        <th className="text-left px-3 py-1 w-12">Sl</th>
                        <th className="text-left px-3 py-1">Client Name</th>
                        <th className="text-left px-3 py-1">Head</th>
                        <th className="text-left px-3 py-1">Under</th>
                        <th className="text-left px-3 py-1 whitespace-nowrap">Billed On</th>
                        <th className="text-right px-3 py-1 whitespace-nowrap">Age (days)</th>
                        <th className="text-left px-3 py-1">Bucket</th>
                        <th className="text-right px-3 py-1 whitespace-nowrap">Outstanding (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {headRows.map((r, i) => {
                        const b = AGE_BUCKETS.find((x) => x.key === r.bucket);
                        const recorded = r.billing_date || (r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—');
                        return (
                          <tr key={r.id} className="hover:bg-gray-50 text-[13px]" data-testid={`ageing-row-${r.id}`}>
                            <td className="px-3 py-0.5 text-gray-500 font-mono text-[12px] tabular-nums">{i + 1}</td>
                            <td className="px-3 py-0.5 font-medium text-gray-900">{r.client_name}</td>
                            <td className="px-3 py-0.5 text-gray-600">{r.category || DEFAULT_CATEGORY}</td>
                            <td className="px-3 py-0.5 text-gray-600">{r.billing_under}</td>
                            <td className="px-3 py-0.5 text-gray-600 tabular-nums">{recorded}</td>
                            <td className="px-3 py-0.5 text-right tabular-nums text-gray-800">{r.ageDays}</td>
                            <td className="px-3 py-0.5">
                              <span className={`inline-block text-[10px] font-mono px-1.5 py-0 border border-current/20 ${b?.tone || ''}`}>{b?.label}</span>
                            </td>
                            <td className="px-3 py-0.5 text-right tabular-nums font-semibold text-red-700">₹{money(r.fee)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
              );
            })()}
          </div>
        </section>
      )}
      {importOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 print:hidden" data-testid="cashflow-import-modal">
          <div className="w-full max-w-2xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] text-gray-500 uppercase">Bulk Import</div>
                <h2 className="font-heading text-xl font-bold text-gray-900">Load the Cashflow Register</h2>
              </div>
              <button
                onClick={() => { setImportOpen(false); setImportResult(null); }}
                className="p-2 text-gray-500 hover:text-gray-900"
                data-testid="cashflow-import-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Option A: One-click seed */}
              <div className="border border-gray-200 p-5 bg-amber-50/30">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-brand-yellow flex items-center justify-center flex-shrink-0">
                    <Database size={18} className="text-gray-900" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-gray-900 text-base">
                      RST &amp; Co. AY 2026-27 · 249 rows
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      One-click import from the master register uploaded on 07 Feb 2026 — 234 rows for RST &amp; Co,
                      14 for SCCS LLP, and 1 for SCCS-Mithun. Re-running is safe: existing (client + entity + AY) rows are skipped.
                    </p>
                    <div className="mt-3">
                      <Button
                        onClick={seedRstAY2026}
                        disabled={importing}
                        className="rounded-none bg-gray-900 hover:bg-black text-white h-9 text-xs px-4"
                        data-testid="cashflow-seed-rst-btn"
                      >
                        {importing ? (
                          <><Loader2 size={13} className="mr-1.5 animate-spin" /> Importing…</>
                        ) : (
                          <><Database size={13} className="mr-1.5" /> Import 249 rows</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Option B: Multi-format file upload */}
              <div className="border border-gray-200 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-brand-blue/10 flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-brand-blue" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-gray-900 text-base">
                      Upload CSV / Excel / Word / PDF
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                      Extracts client rows from tables inside <code className="px-1 bg-gray-100 border border-gray-200 text-[10px] font-mono">.csv .xlsx .docx .pdf</code>.
                      Column names are recognised case-insensitively — <em>client_name / work_alloted_to / task_status /
                      billing_under / fees / payment_status / payment_date / notes / category</em>.
                      Rows without a <b>category</b> column go to the <b>Default Category</b> chosen below (with light
                      keyword auto-tagging for salary / audit / GST / PF-ESI-PT).
                    </p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                      <div>
                        <label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1">Assessment Year</label>
                        <input
                          value={importAY}
                          onChange={(e) => setImportAY(e.target.value)}
                          placeholder="AY 2026-27"
                          className="w-full border border-gray-300 px-3 py-2 text-sm"
                          data-testid="cashflow-import-ay"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1">Default Category</label>
                        <select
                          value={importDefaultCategory}
                          onChange={(e) => setImportDefaultCategory(e.target.value)}
                          className="w-full border border-gray-300 px-3 py-2 text-sm bg-white"
                          data-testid="cashflow-import-default-category"
                        >
                          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <input
                          ref={csvFileRef}
                          type="file"
                          accept=".csv,.xlsx,.xlsm,.docx,.pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                          className="hidden"
                          onChange={(e) => uploadCsv(e.target.files?.[0])}
                          data-testid="cashflow-csv-input"
                        />
                        <Button
                          onClick={() => csvFileRef.current?.click()}
                          disabled={importing}
                          className="rounded-none bg-brand-blue hover:bg-brand-blue-hover text-white h-9 text-xs px-4 w-full"
                          data-testid="cashflow-upload-csv-btn"
                        >
                          {importing ? (
                            <><Loader2 size={13} className="mr-1.5 animate-spin" /> Uploading…</>
                          ) : (
                            <><Upload size={13} className="mr-1.5" /> Choose file</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Uploaded files — history + delete */}
              <div className="border border-gray-200 p-5" data-testid="import-batches-section">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading font-bold text-gray-900 text-base">
                    Uploaded files · {importAY}
                  </div>
                  <button onClick={() => loadImportBatches(importAY)} className="text-xs text-brand-blue hover:underline" data-testid="refresh-batches-btn">
                    Refresh
                  </button>
                </div>
                {importBatches.length === 0 ? (
                  <p className="text-xs text-gray-500">No uploads recorded for this Assessment Year yet. Files you import will appear here so you can remove duplicates.</p>
                ) : (
                  <div className="divide-y divide-gray-100 border border-gray-100">
                    {importBatches.map((b) => (
                      <div key={b.id} className="flex items-center justify-between gap-3 px-3 py-2.5" data-testid={`import-batch-${b.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                            <FileText size={13} className="text-gray-400 shrink-0" /> {b.filename}
                          </div>
                          <div className="text-[11px] text-gray-500 font-mono mt-0.5">
                            {new Date(b.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                            {' · '}<b className="text-gray-700">{b.live_rows}</b> live rows
                            {b.live_rows !== b.inserted && <span className="text-gray-400"> (of {b.inserted} inserted)</span>}
                            {b.default_category && ` · ${b.default_category}`}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteImportBatch(b)}
                          disabled={deletingBatch === b.id}
                          className="shrink-0 inline-flex items-center gap-1.5 text-xs border border-red-200 text-red-600 hover:border-red-500 hover:bg-red-50 px-2.5 py-1.5 disabled:opacity-50"
                          data-testid={`delete-batch-${b.id}`}
                        >
                          {deletingBatch === b.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                          Remove &amp; delete rows
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>


              {/* Result banner */}
              {importResult && (
                <div className={`border p-4 ${importResult.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="cashflow-import-result">
                  {importResult.ok ? (
                    <div className="text-sm text-green-800">
                      <div className="font-bold mb-1">Import complete</div>
                      <div className="text-xs">
                        Source: {importResult.source || 'Bulk import'}
                      </div>
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="bg-white border border-green-200 px-2 py-1.5">
                          <div className="font-mono text-[9px] text-green-700 uppercase tracking-widest">Inserted</div>
                          <div className="font-heading text-lg font-bold text-green-900">{importResult.inserted}</div>
                        </div>
                        <div className="bg-white border border-amber-200 px-2 py-1.5" title="Rows already present in the DB with the same client + entity + AY + date + fees">
                          <div className="font-mono text-[9px] text-amber-700 uppercase tracking-widest">Skipped · DB</div>
                          <div className="font-heading text-lg font-bold text-amber-900">{importResult.skipped_in_db ?? 0}</div>
                        </div>
                        <div className="bg-white border border-orange-200 px-2 py-1.5" title="Duplicate rows appearing twice in the uploaded file itself">
                          <div className="font-mono text-[9px] text-orange-700 uppercase tracking-widest">Skipped · File</div>
                          <div className="font-heading text-lg font-bold text-orange-900">{importResult.skipped_in_file ?? 0}</div>
                        </div>
                        <div className="bg-white border border-gray-200 px-2 py-1.5">
                          <div className="font-mono text-[9px] text-gray-600 uppercase tracking-widest">Total parsed</div>
                          <div className="font-heading text-lg font-bold text-gray-900">{importResult.total}</div>
                        </div>
                      </div>
                      {(importResult.total_extracted_fees != null) && (
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-800 bg-white border border-green-200 px-3 py-1.5">
                          <span className="font-mono text-[9px] text-gray-500 uppercase tracking-widest">Fees reconciliation</span>
                          <span>Parsed <b className="tabular-nums">₹{new Intl.NumberFormat('en-IN').format(Math.round(importResult.total_extracted_fees))}</b></span>
                          <span className="text-gray-400">·</span>
                          <span>Inserted <b className="tabular-nums">₹{new Intl.NumberFormat('en-IN').format(Math.round(importResult.total_inserted_fees || 0))}</b></span>
                          {Math.round((importResult.total_extracted_fees || 0) - (importResult.total_inserted_fees || 0)) !== 0 && (
                            <span className="text-red-600">
                              · Gap ₹{new Intl.NumberFormat('en-IN').format(Math.round((importResult.total_extracted_fees || 0) - (importResult.total_inserted_fees || 0)))} (dropped as duplicate)
                            </span>
                          )}
                        </div>
                      )}
                      {importResult.categories_extracted && importResult.categories_extracted.length > 0 && (
                        <div className="mt-3 text-xs">
                          <div className="font-mono text-[10px] text-green-700 tracking-widest uppercase mb-1">Category routing</div>
                          <div className="flex flex-wrap gap-1.5">
                            {importResult.categories_extracted.map((c) => (
                              <span key={c.category} className="inline-flex items-baseline gap-1 bg-white border border-green-200 px-2 py-0.5">
                                <span className="text-gray-700">{c.category}</span>
                                <span className="font-mono text-[10px] text-green-800 tabular-nums">{c.count}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {importResult.warnings && importResult.warnings.length > 0 && (
                        <details className="mt-2 text-xs">
                          <summary className="cursor-pointer text-amber-700">{importResult.warnings.length} parser note{importResult.warnings.length === 1 ? '' : 's'}</summary>
                          <ul className="mt-1 space-y-0.5 text-amber-800 pl-4 list-disc">
                            {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                          </ul>
                        </details>
                      )}
                      {importResult.errors && importResult.errors.length > 0 && (
                        <details className="mt-3 text-xs">
                          <summary className="cursor-pointer text-red-700">{importResult.errors.length} row-level warning{importResult.errors.length === 1 ? '' : 's'}</summary>
                          <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto text-red-700">
                            {importResult.errors.map((e, i) => (<li key={i} className="font-mono text-[11px]">{e}</li>))}
                          </ul>
                        </details>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-red-700">
                      <div className="font-bold mb-1">Import failed</div>
                      <div className="text-xs">{importResult.error}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <Button onClick={() => { setImportOpen(false); setImportResult(null); }} variant="outline" className="rounded-none bg-white hover:bg-gray-100 text-gray-700 border border-gray-300">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm flex items-stretch justify-end print:hidden">
          <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl" data-testid="cashflow-editor">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] text-gray-500 uppercase">
                  {editing.id ? 'Editing' : 'New'} Entry
                </div>
                <h2 className="font-heading text-xl font-bold text-gray-900">{editing.client_name || 'Untitled'}</h2>
              </div>
              <button onClick={closeEdit} className="p-2 text-gray-500 hover:text-gray-900"><X size={20} /></button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Category*">
                  <select
                    value={editing.category || DEFAULT_CATEGORY}
                    onChange={(e) => {
                      const nextCat = e.target.value;
                      // Auto-switch entity default only when the current value
                      // is still a default (never override a manual choice).
                      const wasDefault = !editing.billing_under
                        || Object.values(DEFAULT_ENTITY_BY_CATEGORY).includes(editing.billing_under)
                        || editing.billing_under === 'RST & Co';
                      setEditing({
                        ...editing,
                        category: nextCat,
                        billing_under: wasDefault ? defaultEntityFor(nextCat) : editing.billing_under,
                      });
                    }}
                    className="cf-input"
                    data-testid="cf-editor-category"
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Assessment Year*">
                  <input value={editing.assessment_year} onChange={(e) => setEditing({ ...editing, assessment_year: e.target.value })}
                    className="cf-input" placeholder="AY 2026-27" data-testid="cf-editor-ay" />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Billing Under*">
                  <select value={editing.billing_under} onChange={(e) => setEditing({ ...editing, billing_under: e.target.value })}
                    className="cf-input" data-testid="cf-editor-entity">
                    {Array.from(new Set([...ENTITY_OPTIONS, editing.billing_under].filter(Boolean))).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Client Name*">
                  <input value={editing.client_name} onChange={(e) => setEditing({ ...editing, client_name: e.target.value })}
                    className="cf-input" data-testid="cf-editor-client" />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <Field label="Work Alloted To">
                  <input value={editing.work_alloted_to || ''} onChange={(e) => setEditing({ ...editing, work_alloted_to: e.target.value })}
                    className="cf-input" data-testid="cf-editor-work" />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Fees (₹)">
                  <input type="number" min="0" step="1" value={editing.fees}
                    onChange={(e) => setEditing({ ...editing, fees: e.target.value })}
                    className="cf-input tabular-nums" data-testid="cf-editor-fees" />
                </Field>
                <Field label="Payment Status">
                  <select value={editing.payment_status} onChange={(e) => setEditing({ ...editing, payment_status: e.target.value })} className="cf-input" data-testid="cf-editor-pstatus">
                    <option value="Pending">Pending</option>
                    <option value="Received">Received</option>
                  </select>
                </Field>
                <Field label="Payment Date">
                  <input type="date" value={toISODate(editing.payment_date || '')}
                    onChange={(e) => setEditing({ ...editing, payment_date: fromISODate(e.target.value) })}
                    className="cf-input" />
                </Field>
              </div>

              <Field label="Date of Billing">
                <input type="date" value={toISODate(editing.billing_date || '')}
                  onChange={(e) => setEditing({ ...editing, billing_date: fromISODate(e.target.value) })}
                  className="cf-input md:max-w-[240px]" data-testid="cf-editor-billing-date" />
                <p className="text-[11px] text-gray-400 mt-1">Used to age this receivable in the Ageing Schedule.</p>
              </Field>

              <Field label="Notes">
                <textarea value={editing.notes || ''} rows={2}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  className="cf-input" />
              </Field>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{error}</div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <Button onClick={closeEdit} variant="outline" className="rounded-none bg-white hover:bg-gray-100 text-gray-700 border border-gray-300">Cancel</Button>
              <Button onClick={save} disabled={saving} className="rounded-none bg-brand-blue hover:bg-brand-blue-hover text-white" data-testid="cf-save-btn">
                {saving ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</>) : (<><Save size={14} className="mr-2" /> Save</>)}
              </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cf-input { width: 100%; border: 1px solid #d1d5db; padding: 9px 11px; font-size: 14px; outline: none; background: white; }
        .cf-input:focus { border-color: #111827; }
        @media print {
          body { background: white; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
};

const KPI = ({ label, value, sub, icon, tone }) => {
  const toneMap = {
    yellow: 'bg-brand-yellow/10 text-brand-yellow',
    green: 'bg-green-500/10 text-green-400',
    red: 'bg-red-500/10 text-red-400',
    mono: 'bg-white/10 text-white/80',
  };
  return (
    <div className="bg-[#0a0f1e] p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono text-[10px] tracking-[0.25em] text-white/50 uppercase">{label}</div>
        <div className={`w-7 h-7 ${toneMap[tone] || toneMap.mono} flex items-center justify-center`}>{icon}</div>
      </div>
      <div className="font-heading text-2xl md:text-3xl font-black text-white tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-white/50">{sub}</div>}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-mono tracking-widest text-gray-600 uppercase mb-2">{label}</label>
    {children}
  </div>
);

const todayDisplay = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

const SortArrow = ({ active, dir }) => (
  <span className={`inline-flex flex-col justify-center text-[8px] leading-none ${active ? 'text-brand-yellow' : 'text-white/40'}`}>
    <span className={active && dir === 'asc' ? 'opacity-100' : 'opacity-40'}>▲</span>
    <span className={active && dir === 'desc' ? 'opacity-100' : 'opacity-40'}>▼</span>
  </span>
);

export default CashflowPage;
